<template>
	<div class="dialog draggable-item bg-light light w-100-sm w-100-md w-75-lg w-50-xl h-75">
		<div class="dialog-title pl-10">
			{{ !customer.id ? $t('Create new customer') : customer.company_name }}
			<span class="button square closer" @click="$root.$emit('CLOSECUSTOMERDIALOG')"></span>
		</div>
		<div class="dialog-content overflow">
			<div class="form-group">
				<h6 class="border-bottom pb-1 mt-1 text-bold">{{ $t("Company information") }}</h6>
				<div>
					<label class="text-bold mb-1 d-block">{{ $t("Company name") }}</label>
					<input class="input w-100 mb-2" v-model="customer.company_name" maxlength="200" />
				</div>
				<div>
					<label class="text-bold mb-1 d-block">{{ $t("VAT") }}</label>
					<input class="input w-100 mb-2" v-model="customer.vat_number" maxlength="20" />
				</div>
			</div>


			<div class="form-group">
				<h6 class="border-bottom pb-1 mt-3 text-bold">{{ $t("Address information") }}</h6>
				<div class="d-flex flex-justify-between">
					<div class="w-80">
						<label class="text-bold mb-1 d-block">{{ $t("Address") }}</label>
						<input class="input w-100 mb-2" v-model="customer.address" maxlength="300" />
					</div>
					<div class="w-10">
						<label class="text-bold mb-1 d-block">{{ $t("ZIP") }}</label>
						<input class="input w-100 mb-2" v-model="customer.postal_code" maxlength="10" />
					</div>
				</div>
				<div class="d-flex flex-justify-between">

					<div class="w-80">
						<label class="text-bold mb-1 d-block">{{ $t("City") }}</label>
						<input class="input w-100 mb-2" v-model="customer.city" maxlength="100" />
					</div>
					<div class="w-10">
						<label class="text-bold mb-1 d-block">{{ $t("Province") }}</label>
						<input class="input w-100 mb-2" v-model="customer.province" maxlength="2" />
					</div>
				</div>
				<div class="d-flex flex-justify-between">
					<div class="w-60">
						<label class="text-bold mb-1 d-block">{{ $t("Country") }}</label>
						<select class="input w-100 mb-2" v-model="customer.country" @change="onCountryChange">
							<option value=""></option>
							<option v-for="(symbol, name) in COUNTRIES" :key="name" :value="name">
								{{ name }}
							</option>
						</select>
					</div>
					<div class="w-10">
						<label class="text-bold mb-1 d-block">{{ $t("Currency") }}</label>
						<select class="input w-100 mb-2" v-model="customer.currency">
							<option value=""></option>
							<option v-for="c in CUSTOMER_CURRENCY" :key="c" :value="c">{{ c }}</option>
						</select>
					</div>

					<div class="w-20">
						<label class="text-bold mb-1 d-block">{{ $t("Hourly rate") }}</label>
						<input class="input w-100 mb-2" v-model.number="customer.hourly_rate" type="number" step="0.01" :disabled="!hasCustomerCurrency" />
					</div>
				</div>
			</div>
			<div class="form-group">

				<h6 class="border-bottom pb-1 mt-3 text-bold">{{ $t("Billing information") }}</h6>
				<div>
					<label class="text-bold mb-1 d-block">{{$t("Email")}}/{{ $t("PEC") }}</label>
					<input class="input w-100 mb-2" v-model="customer.pec_email" maxlength="200" />
				</div>
				<div>
					<label class="text-bold mb-1 d-block">{{ $t("SDI code") }}</label>
					<input class="input w-100 mb-2" v-model="customer.sdi_code" maxlength="10" />
				</div>

			</div>

			<div class="form-group">
				<h6 class="border-bottom pb-1 mt-3 text-bold">{{ $t("Assigned groups") }}</h6>
				<select v-if="groups && groups.length > 0" data-role="select" data-filter="true" multiple :data-filter-placeholder="$t('Search for groups')" data-cls-selected-item="bg-light border pr-2 rounded" data-cls-selected-item-remover="bg-transparent border-0 d-flex flex-align-center" class="input" id="customergroupselect" ref="groupsSelect" @change="onGroupsChange">
					<option v-for="g in groups" :value="g.id_group" :key="g.id_group" :selected="isGroupSelected(g.id_group)">
						{{ g.name_group }}
					</option>
				</select>
				<div v-else class="text-small fg-dark mb-2">{{ $t("No groups available") }}</div>
			</div>
		</div>
		<div class="dialog-actions p-3 border-top text-right">
			<button class="button" @click="$root.$emit('CLOSECUSTOMERDIALOG')">{{ $t("Cancel") }}</button>
			<button class="button secondary ml-2" :disabled="!enableSaveButton" v-disable-tooltip:saveButton_disabled_message @click="save">
				{{ $t("Save") }}
			</button>
		</div>
	</div>
</template>

<script>
import { COUNTRIES, CUSTOMER_CURRENCY, getCurrencyByCountry } from '../../../utils/entityMethods/customer';


export default {
	name: "CustomerDialog",
	props: {
		customer: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			COUNTRIES: COUNTRIES,
			CUSTOMER_CURRENCY: CUSTOMER_CURRENCY,
			saveButton_disabled_message: "",
			groups: [],
			originalGroupIds: []
		};
	},
	computed: {
		hasCustomerCurrency() {
			return !!this.customer.currency;
		},
		enableSaveButton() {
			if (this.customer.name == "") {
				this.saveButton_disabled_message = this.$t("Insert a name");
				return false;
			}

			if (this.customer.address == "" || this.customer.city == "" || this.customer.province == "" || this.customer.country == "") {
				this.saveButton_disabled_message = this.$t("Enter a valid address");
				return false;
			}

			if (this.customer.pec_email == "" || !this.$root.isValidEmail(this.customer.pec_email)) {
				this.saveButton_disabled_message = this.$t("Enter a valid email address");
				return false;
			}
			this.saveButton_disabled_message = "";
			return true;
		}
	},
	watch: {
		"customer.currency"() {
			this.normalizeHourlyRate();
		}
	},
	created() {
		//Non devo mostrare i gruppi già assegnati
		this.groups = this.$session.getGroups().filter(g => g.id_customer == null || (this.customer.id != null && this.customer.id == g.id_customer));
		this.normalizeHourlyRate();
		if (this.customer.id) {
			this.originalGroupIds = this.groups
				.filter(g => g.id_customer == this.customer.id)
				.map(g => g.id_group);
			this.customer.ids_groups = [...this.originalGroupIds];
		}
	},
	methods: {
		isGroupSelected(idGroup) {
			return this.customer.ids_groups.includes(idGroup);
		},
		// Al cambio del paese, auto-imposta la valuta col codice ISO 4217 mappato.
		// Se il paese non ha una valuta nota, lascia invariata quella corrente.
		onCountryChange() {
			const code = getCurrencyByCountry(this.customer.country);
			if (code) this.customer.currency = code;
		},
		onGroupsChange(e) {
			this.customer.ids_groups = Array.from(e.target.selectedOptions).map(o => o.value);
		},
		normalizeHourlyRate() {
			if (!this.hasCustomerCurrency) this.customer.hourly_rate = null;
		},

		async save() {
			this.normalizeHourlyRate();
			let saved;
			if (this.customer.id) {
				saved = await this.$api.editCustomer(this.customer);
			} else {
				saved = await this.$api.createCustomer(this.customer);
			}
			if (!saved) return;

			this.$session.setGroups(await this.$api.getGroups());

			const newCustomerId = saved.id || this.customer.id;
			this.$root.$emit("refreshCustomers");
			this.$root.$emit("CLOSECUSTOMERDIALOG");
		}
	}
};
</script>
